// Helpers + Firestore read layer for the Teams and Players records.
//
// Teams and players are extracted from each match during migration (and when a
// new match is saved). The model:
//   - A player's identity is its case-insensitive first + last name. Players
//     with the same first/last name (ignoring case) are treated as the SAME
//     person and stored once per user in `players/{uid}_<first>_<last>`.
//   - A team is identified by its name AND its roster. Two teams that share a
//     name but have different rosters are stored as SEPARATE documents
//     (`teams/{uid}_<nameSlug>_<rosterHash>`), so they don't get merged.

import { getAuth } from "firebase/auth";
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase.config";

function currentUid() {
  return getAuth().currentUser?.uid ?? null;
}

function currentEmail() {
  return getAuth().currentUser?.email ?? null;
}

// Lowercased, punctuation-stripped slug used for both ids and case-insensitive
// name comparison.
export function slugifyTP(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

// Read first/last/nick/shirt from the various shapes used by the DVW and PSVB
// parsers (FirstName/firstName/playerFirstName, etc.).
export function normalizePlayer(p) {
  if (!p)
    return {
      firstName: "",
      lastName: "",
      nickName: "",
      shirtNumber: "",
      positions: [],
    };
  const firstName = String(
    p.FirstName ?? p.firstName ?? p.playerFirstName ?? ""
  ).trim();
  const lastName = String(
    p.LastName ?? p.lastName ?? p.playerLastName ?? ""
  ).trim();
  const nickName = String(p.NickName ?? p.nickName ?? "").trim();
  const shirtRaw = p.shirtNumber ?? p.ShirtNumber ?? p.playerNumber ?? "";
  const shirtNumber =
    shirtRaw === null || shirtRaw === undefined ? "" : String(shirtRaw).trim();
  // Playing positions (numeric codes: 1=Libero, 2=Pass/Hitter, 3=Opposite,
  // 4=Middle, 5=Setter — see DVWFile.js). Deduped, invalid/blank dropped.
  const positionsRaw = Array.isArray(p.Positions)
    ? p.Positions
    : Array.isArray(p.positions)
      ? p.positions
      : [];
  const positions = [
    ...new Set(
      positionsRaw
        .map((v) => parseInt(v, 10))
        .filter((n) => Number.isFinite(n) && n > 0)
    ),
  ];
  return { firstName, lastName, nickName, shirtNumber, positions };
}

// Case-insensitive identity key for a player. Empty when the player has no
// real name (only a shirt number) — such entries are not stored as players.
export function playerIdentity(firstName, lastName) {
  const f = slugifyTP(firstName);
  const l = slugifyTP(lastName);
  if (!f && !l) return "";
  return `${f}|${l}`;
}

export function playerDocId(uid, firstName, lastName) {
  return `${uid}_${slugifyTP(firstName)}_${slugifyTP(lastName)}`;
}

// Stable short hash (djb2 -> base36) of a team's roster, used to distinguish
// same-named teams with different players.
function hashString(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

export function rosterHash(identities) {
  const sorted = [...identities].filter(Boolean).sort();
  if (sorted.length === 0) return "empty";
  return hashString(sorted.join(","));
}

export function teamDocId(uid, name, hash) {
  return `${uid}_${slugifyTP(name)}_${hash}`;
}

/**
 * Extract the two teams (and their players) from a parsed match and merge them
 * into the supplied accumulator Maps (so teams/players are deduplicated across
 * many matches). Mutates `teamsMap` and `playersMap`.
 *
 * @param {object} parsedMatch  match with teamA/teamB { Name, players: [] }
 * @param {object} ctx          { uid, email, origin, sessionId, seconds }
 * @param {Map}    teamsMap     teamDocId -> team document
 * @param {Map}    playersMap   playerDocId -> player document
 */
export function collectTeamsAndPlayers(parsedMatch, ctx, teamsMap, playersMap) {
  const { uid, email, origin, sessionId, seconds } = ctx;
  for (const team of [parsedMatch?.teamA, parsedMatch?.teamB]) {
    if (!team) continue;
    const name = String(team.Name ?? team.name ?? "").trim();
    if (!name) continue;

    const normalized = (team.players ?? []).map(normalizePlayer);
    // Only players with a real name count toward identity / the roster hash.
    const named = normalized.filter(
      (p) => playerIdentity(p.firstName, p.lastName) !== ""
    );
    const identities = named.map((p) =>
      playerIdentity(p.firstName, p.lastName)
    );
    const hash = rosterHash(identities);
    const tId = teamDocId(uid, name, hash);

    const existing = teamsMap.get(tId);
    if (existing) {
      // Keep the most recent match as the "last seen" hint.
      if ((seconds ?? 0) > (existing.lastSeenSeconds ?? 0)) {
        existing.lastSeenSeconds = seconds ?? existing.lastSeenSeconds;
        existing.lastSessionId = sessionId ?? existing.lastSessionId;
        existing.origin = origin ?? existing.origin;
      }
    } else {
      teamsMap.set(tId, {
        id: tId,
        name,
        nameLower: slugifyTP(name),
        rosterHash: hash,
        playerCount: named.length,
        players: named.map((p) => ({
          firstName: p.firstName,
          lastName: p.lastName,
          nickName: p.nickName,
          shirtNumber: p.shirtNumber,
          positions: p.positions ?? [],
          playerId: playerDocId(uid, p.firstName, p.lastName),
        })),
        playerIds: named.map((p) =>
          playerDocId(uid, p.firstName, p.lastName)
        ),
        origin: origin ?? null,
        lastSessionId: sessionId ?? null,
        lastSeenSeconds: seconds ?? null,
        owner: uid,
        ownerEmail: email ?? null,
      });
    }

    // Players (deduped per user by case-insensitive first+last name).
    for (const p of named) {
      const pId = playerDocId(uid, p.firstName, p.lastName);
      const ex = playersMap.get(pId);
      if (ex) {
        if (!ex.teamIds.includes(tId)) ex.teamIds.push(tId);
        if (!ex.teamNames.includes(name)) ex.teamNames.push(name);
        // Backfill a nickname if we didn't have one yet.
        if (!ex.nickName && p.nickName) ex.nickName = p.nickName;
        // Union any playing positions seen across matches.
        if (p.positions?.length) {
          ex.positions = [...new Set([...(ex.positions ?? []), ...p.positions])];
        }
      } else {
        playersMap.set(pId, {
          id: pId,
          firstName: p.firstName,
          lastName: p.lastName,
          nickName: p.nickName,
          positions: p.positions ?? [],
          nameLower: playerIdentity(p.firstName, p.lastName),
          teamIds: [tId],
          teamNames: [name],
          owner: uid,
          ownerEmail: email ?? null,
        });
      }
    }
  }
}

// ---- Reads for the Teams & Players page ----------------------------------

export async function fetchAllTeams() {
  const uid = currentUid();
  if (!uid) return [];
  const q = query(collection(db, "teams"), where("owner", "==", uid));
  const snap = await getDocs(q);
  const teams = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    // The same `teams` collection also holds match-setup convenience records
    // written by `fsStoreTeam` (doc id `{uid}_{slug(name)}`, no roster-hash
    // suffix) — those store `players`/`lineup` as JSON strings and lack the
    // `playerIds`/`rosterHash` fields this roster-management page relies on.
    // Only include genuine roster docs (`players` is always a real array
    // here — see `collectTeamsAndPlayers`/`createTeam`/`updateTeam`).
    .filter((t) => Array.isArray(t.players));
  teams.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return teams;
}

export async function fetchAllPlayers() {
  const uid = currentUid();
  if (!uid) return [];
  const q = query(collection(db, "players"), where("owner", "==", uid));
  const snap = await getDocs(q);
  const players = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  players.sort((a, b) => {
    const al = `${a.lastName} ${a.firstName}`.trim().toLowerCase();
    const bl = `${b.lastName} ${b.firstName}`.trim().toLowerCase();
    return al.localeCompare(bl);
  });
  return players;
}

// ---- Manual create / edit (Teams & Players page) -------------------------

// Create or update a player record from manual input. Existing team links are
// preserved (we never clobber teamIds/teamNames).
export async function createPlayer({ firstName, lastName, nickName }) {
  const uid = currentUid();
  if (!uid) return null;
  const f = String(firstName ?? "").trim();
  const l = String(lastName ?? "").trim();
  if (playerIdentity(f, l) === "") return null; // a real name is required
  const id = playerDocId(uid, f, l);
  const ref = doc(db, "players", id);
  const snap = await getDoc(ref);
  const base = {
    firstName: f,
    lastName: l,
    nickName: String(nickName ?? "").trim(),
    nameLower: playerIdentity(f, l),
    owner: uid,
    ownerEmail: currentEmail(),
  };
  if (!snap.exists()) {
    base.teamIds = [];
    base.teamNames = [];
  }
  await setDoc(ref, base, { merge: true });
  return { id, teamIds: [], teamNames: [], ...(snap.data() ?? {}), ...base };
}

// Merge a player doc so it references the given team (used when a player is
// added to a team).
async function linkPlayerToTeam(uid, p, teamId, teamName) {
  const id = playerDocId(uid, p.firstName, p.lastName);
  await setDoc(
    doc(db, "players", id),
    {
      firstName: String(p.firstName ?? "").trim(),
      lastName: String(p.lastName ?? "").trim(),
      nickName: String(p.nickName ?? "").trim(),
      nameLower: playerIdentity(p.firstName, p.lastName),
      teamIds: arrayUnion(teamId),
      teamNames: arrayUnion(teamName),
      owner: uid,
      ownerEmail: currentEmail(),
    },
    { merge: true }
  );
}

// Create a new team (with an optional starting roster) and the matching player
// records. Players supplied as plain objects ({firstName,lastName,nickName,
// shirtNumber}) are normalized; entries without a real name are ignored.
export async function createTeam({ name, players }) {
  const uid = currentUid();
  if (!uid) return null;
  const nm = String(name ?? "").trim();
  if (!nm) return null;
  const named = (players ?? [])
    .map(normalizePlayer)
    .filter((p) => playerIdentity(p.firstName, p.lastName) !== "");
  const hash = rosterHash(
    named.map((p) => playerIdentity(p.firstName, p.lastName))
  );
  const id = teamDocId(uid, nm, hash);
  const embedded = named.map((p) => ({
    firstName: String(p.firstName ?? "").trim(),
    lastName: String(p.lastName ?? "").trim(),
    nickName: String(p.nickName ?? "").trim(),
    shirtNumber: String(p.shirtNumber ?? "").trim(),
    playerId: playerDocId(uid, p.firstName, p.lastName),
  }));
  const data = {
    id,
    name: nm,
    nameLower: slugifyTP(nm),
    rosterHash: hash,
    playerCount: embedded.length,
    players: embedded,
    playerIds: embedded.map((p) => p.playerId),
    origin: "New",
    lastSessionId: null,
    lastSeenSeconds: Math.floor(Date.now() / 1000),
    owner: uid,
    ownerEmail: currentEmail(),
  };
  await setDoc(doc(db, "teams", id), data, { merge: true });
  for (const p of named) {
    await linkPlayerToTeam(uid, p, id, nm);
  }
  return data;
}

// Add (or update the shirt number of) a player on an existing team. The team's
// document id is kept stable; only its roster fields are updated.
export async function addPlayerToTeam(team, playerInput) {
  const uid = currentUid();
  if (!uid || !team?.id) return null;
  const np = normalizePlayer(playerInput);
  if (playerIdentity(np.firstName, np.lastName) === "") return null;
  const pId = playerDocId(uid, np.firstName, np.lastName);
  const players = (team.players ?? []).map((x) => ({
    firstName: x.firstName,
    lastName: x.lastName,
    nickName: x.nickName,
    shirtNumber: x.shirtNumber,
    playerId: x.playerId ?? playerDocId(uid, x.firstName, x.lastName),
  }));
  const existing = players.find((x) => x.playerId === pId);
  if (existing) {
    existing.shirtNumber = np.shirtNumber || existing.shirtNumber;
    existing.nickName = np.nickName || existing.nickName;
  } else {
    players.push({
      firstName: np.firstName,
      lastName: np.lastName,
      nickName: np.nickName,
      shirtNumber: np.shirtNumber,
      playerId: pId,
    });
  }
  const hash = rosterHash(
    players.map((p) => playerIdentity(p.firstName, p.lastName))
  );
  const data = {
    players,
    playerIds: players.map((p) => p.playerId),
    playerCount: players.length,
    rosterHash: hash,
  };
  await setDoc(doc(db, "teams", team.id), data, { merge: true });
  await linkPlayerToTeam(uid, np, team.id, team.name);
  return { ...team, ...data };
}

// Update an existing team's name and full roster in place. The document id is
// kept stable so playlists/reports linked to the team remain valid. Players on
// the new roster are linked back to the team; entries without a real name are
// ignored.
export async function updateTeam(team, { name, players }) {
  const uid = currentUid();
  if (!uid || !team?.id) return null;
  const nm = String(name ?? "").trim();
  if (!nm) return null;
  const named = (players ?? [])
    .map(normalizePlayer)
    .filter((p) => playerIdentity(p.firstName, p.lastName) !== "");
  const embedded = named.map((p) => ({
    firstName: p.firstName,
    lastName: p.lastName,
    nickName: p.nickName,
    shirtNumber: p.shirtNumber,
    playerId: playerDocId(uid, p.firstName, p.lastName),
  }));
  const hash = rosterHash(
    embedded.map((p) => playerIdentity(p.firstName, p.lastName))
  );
  const data = {
    name: nm,
    nameLower: slugifyTP(nm),
    rosterHash: hash,
    playerCount: embedded.length,
    players: embedded,
    playerIds: embedded.map((p) => p.playerId),
  };
  await setDoc(doc(db, "teams", team.id), data, { merge: true });
  for (const p of named) {
    await linkPlayerToTeam(uid, p, team.id, nm);
  }
  return { ...team, ...data };
}

// Merge multiple team documents (from different scouts/matches) into a single
// canonical team. mergedRoster is an array of {firstName, lastName, nickName,
// shirtNumber} chosen by the user. sourceTeamIds is the list of team doc IDs
// to consolidate and delete.
export async function mergeTeams({ mergedName, mergedRoster, sourceTeamIds }) {
  const uid = currentUid();
  if (!uid) return null;
  const sourceSet = new Set(sourceTeamIds.map(String));

  // 1. Create the merged team (also writes player docs via linkPlayerToTeam).
  const created = await createTeam({ name: mergedName, players: mergedRoster });
  if (!created) return null;
  const mergedTeamId = created.id;

  // Build shirt# → canonical playerId map for remapping assignments.
  const shirtToNewPlayerId = new Map();
  for (const p of created.players ?? []) {
    const shirt = String(p.shirtNumber ?? "").trim();
    if (shirt) shirtToNewPlayerId.set(shirt, p.playerId);
  }

  // Build oldPlayerId → newPlayerId by scanning source team rosters.
  const oldToNew = new Map();
  for (const tid of sourceTeamIds) {
    if (tid === mergedTeamId) continue;
    const snap = await getDoc(doc(db, "teams", tid));
    if (!snap.exists()) continue;
    for (const p of snap.data().players ?? []) {
      const shirt = String(p.shirtNumber ?? "").trim();
      const newId = shirt ? shirtToNewPlayerId.get(shirt) : null;
      if (newId && p.playerId && p.playerId !== newId) {
        oldToNew.set(p.playerId, newId);
      }
    }
  }

  // 2. Clean up player documents: strip source-team refs; delete orphans.
  const mergedPlayerIds = new Set(created.playerIds ?? []);
  const pSnap = await getDocs(
    query(collection(db, "players"), where("owner", "==", uid))
  );
  for (const d of pSnap.docs) {
    const data = d.data();
    const teamIds = data.teamIds ?? [];
    const teamNames = data.teamNames ?? [];
    if (!teamIds.some((tid) => sourceSet.has(String(tid)))) continue;

    const keepIdx = teamIds
      .map((tid, i) => (sourceSet.has(String(tid)) ? -1 : i))
      .filter((i) => i >= 0);
    const newTeamIds = keepIdx.map((i) => teamIds[i]);
    const newTeamNames = keepIdx.map((i) => teamNames[i]);

    if (mergedPlayerIds.has(d.id)) {
      // Canonical player: remove old team IDs but KEEP old team names so that
      // matches coded under the source team names are still visible on the
      // player's profile (matches reference team names, not IDs).
      // linkPlayerToTeam already added the new merged team name via arrayUnion.
      await setDoc(d.ref, { teamIds: newTeamIds }, { merge: true });
    } else if (newTeamIds.length === 0) {
      // Orphan — only existed in the merged teams.
      await deleteDoc(d.ref);
    } else {
      await setDoc(d.ref, { teamIds: newTeamIds, teamNames: newTeamNames }, { merge: true });
    }
  }

  // 3. Remap playlistAssignments that pointed to old team/player IDs.
  const PLAYLIST_ASSIGN = "playlistAssignments";
  const paSnap = await getDocs(
    query(collection(db, PLAYLIST_ASSIGN), where("owner", "==", uid))
  );
  for (const d of paSnap.docs) {
    const data = d.data();
    if (data.scope === "team" && sourceSet.has(String(data.refId)) && data.refId !== mergedTeamId) {
      const newDocId = `${uid}_team_${mergedTeamId}_${data.playlistId}`;
      await setDoc(doc(db, PLAYLIST_ASSIGN, newDocId), { ...data, refId: mergedTeamId });
      await deleteDoc(d.ref);
    } else if (data.scope === "player" && oldToNew.has(data.refId)) {
      const newRefId = oldToNew.get(data.refId);
      const newDocId = `${uid}_player_${newRefId}_${data.playlistId}`;
      await setDoc(doc(db, PLAYLIST_ASSIGN, newDocId), { ...data, refId: newRefId });
      await deleteDoc(d.ref);
    }
  }

  // 4. Delete source team documents.
  for (const tid of sourceTeamIds) {
    if (tid !== mergedTeamId) {
      await deleteDoc(doc(db, "teams", tid));
    }
  }

  return created;
}
